import { Injectable, Optional } from '@angular/core';
import { Subscription } from 'rxjs';
import { filter, distinctUntilChanged, take, timeout } from 'rxjs/operators';
import { SocketService } from '../../event/services/socket.service';
import { ConnectionMonitorService } from './connection-monitor.service';
import { ReconnectionService } from './reconnection.service';
import { AnimationRecoveryService } from './animation-recovery.service';
import { ConnectionState, ConnectionLogger } from '../models/connection-state.model';
import { DatadogService } from '../../observability/datadog.service';
import { environment } from '../../../../environments/environment';

/**
 * WebSocket Health Service - Facade Pattern
 * 
 * Responsabilidades:
 * - Orquestrar todos os serviços de conexão
 * - Coordenar fluxo de recovery completo
 * - Fornecer interface unificada para o app
 * - Gerenciar ciclo de vida de reconexão
 * - Cleanup adequado de recursos
 * 
 * Princípios SOLID:
 * - SRP: Coordenação de health check e recovery
 * - OCP: Extensível através de novos serviços
 * - DIP: Depende de abstrações (serviços injetados)
 * - LSP: Substituível por implementações alternativas
 * - ISP: Interface simples (initialize/destroy)
 * 
 * Design Pattern: Facade
 * - Simplifica interface complexa de múltiplos serviços
 * - Centraliza lógica de orquestração
 * 
 * MELHORIAS:
 * ✅ Cleanup adequado (para loop de reconexão)
 * ✅ Verificação de conexão inicial
 * ✅ Logs configuráveis
 * ✅ Gestão de subscriptions
 */
@Injectable({
  providedIn: 'root',
})
export class WebSocketHealthService {
  private readonly logger: ConnectionLogger;
  
  private connectionStateSubscription?: Subscription;
  private reconnectionInProgress = false;
  private isDestroyed = false; // Flag de cleanup

  constructor(
    private socketService: SocketService,
    private connectionMonitor: ConnectionMonitorService,
    private reconnectionService: ReconnectionService,
    private animationRecovery: AnimationRecoveryService,
    @Optional() private datadogService?: DatadogService
  ) {
    this.logger = new ConnectionLogger(
      environment.connection.enableDebugLogs,
      this.datadogService
    );
  }

  /**
   * Inicializa o sistema de health check
   * 
   * MELHORIA: Verifica se realmente conectou antes de iniciar heartbeat
   */
  initialize(): void {
    if (this.isDestroyed) {
      this.logger.warn('⚠️ Serviço já foi destruído, não pode inicializar');
      return;
    }

    this.logger.log('🚀 Inicializando WebSocket Health System');
    
    this.isDestroyed = false;

    // Conectar socket
    this.socketService.connect();

    // CORREÇÃO: Aguardar REALMENTE conectar antes de iniciar heartbeat
    this.waitForInitialConnection();
  }

  /**
   * Aguarda conexão inicial com timeout
   * 
   * CRÍTICO: Não assume que conectou, aguarda evento real
   */
  private waitForInitialConnection(): void {
    this.logger.log('⏳ Aguardando conexão inicial...');

    this.socketService.onConnected$
      .pipe(
        take(1),
        timeout(10000) // 10s timeout
      )
      .subscribe({
        next: () => {
          this.logger.log('✅ Conexão inicial estabelecida');
          
          // Agora sim, iniciar monitoramento
          this.connectionMonitor.startMonitoring();
          
          // Escutar mudanças de estado
          this.subscribeToConnectionState();
        },
        error: (error) => {
          this.logger.error('❌ Falha na conexão inicial, iniciando reconexão...', error);
          
          // Conexão inicial falhou, ir direto para reconexão
          this.connectionMonitor.setConnectionState('disconnected');
          this.subscribeToConnectionState();
          this.handleDisconnection();
        },
      });
  }

  /**
   * Destroi e limpa recursos
   * 
   * CORREÇÃO: Para loop de reconexão
   */
  destroy(): void {
    this.logger.log('🛑 Destruindo WebSocket Health System');
    
    // Sinalizar que está destruído (para loop de reconexão)
    this.isDestroyed = true;
    this.reconnectionInProgress = false;

    // Parar monitoramento
    this.connectionMonitor.destroy();
    
    // Cancelar subscriptions
    this.connectionStateSubscription?.unsubscribe();
    
    // Desconectar socket
    this.socketService.disconnect();

    this.logger.log('✅ WebSocket Health System destruído');
  }

  /**
   * Escuta mudanças de estado de conexão e reage
   */
  private subscribeToConnectionState(): void {
    this.connectionStateSubscription = this.connectionMonitor.connectionState$
      .pipe(
        distinctUntilChanged() // Apenas quando estado mudar
      )
      .subscribe((state: ConnectionState) => {
        this.logger.log(`📡 Estado de conexão: ${state}`);

        switch (state) {
          case 'disconnected':
            this.handleDisconnection();
            break;

          case 'connected':
            this.handleReconnection();
            break;

          case 'reconnecting':
            // Estado intermediário, não fazer nada
            break;
        }
      });
  }

  /**
   * Trata desconexão detectada
   */
  private handleDisconnection(): void {
    // Verificar se foi destruído
    if (this.isDestroyed) {
      this.logger.log('⏹️ Serviço destruído, não tentando reconectar');
      return;
    }

    this.logger.warn('⚠️ Desconexão detectada!');
    // 📊 Enviar métrica para Datadog
    this.datadogService?.trackAction('websocket.disconnected', {
      reconnectionAttempts: this.reconnectionService.getCurrentAttempts(),
      metrics: this.connectionMonitor.getMetrics(),
    });
    // 1. Limpar animações travadas
    this.animationRecovery.performFullRecovery();

    // 2. Iniciar processo de reconexão (se não já em progresso)
    if (!this.reconnectionInProgress) {
      this.startReconnectionLoop();
    }
  }

  /**
   * Trata reconexão bem-sucedida
   */
  private handleReconnection(): void {
    if (this.reconnectionInProgress) {
      this.logger.log('✅ Reconexão bem-sucedida!');
            const metrics = this.connectionMonitor.getMetrics();
      
      // 📊 Enviar métrica para Datadog
      this.datadogService?.trackAction('websocket.reconnected', {
        attempts: this.reconnectionService.getCurrentAttempts(),
        metrics,
        downtimeMs: metrics.totalDowntime,
        averageReconnectTimeMs: metrics.averageReconnectTime,
      });
            // Parar loop de reconexão
      this.reconnectionInProgress = false;
      
      // Resetar contador de tentativas
      this.reconnectionService.resetAttempts();
      
      // Reiniciar monitoramento de heartbeat
      this.connectionMonitor.startMonitoring();
      
      this.logger.log('🔄 Sistema normalizado');
    }
  }

  /**
   * Loop de reconexão infinito com backoff exponencial
   * 
   * CORREÇÃO: Verifica isDestroyed para parar
   */
  private startReconnectionLoop(): void {
    // Verificar se foi destruído
    if (this.isDestroyed) {
      this.logger.log('⏹️ Serviço destruído durante início de reconexão');
      return;
    }

    this.reconnectionInProgress = true;
    this.connectionMonitor.setConnectionState('reconnecting');

    this.logger.log('🔄 Iniciando loop de reconexão...');

    // Tentar reconectar
    this.attemptReconnection();
  }

  /**
   * Tenta reconectar uma vez
   * 
   * MODIFICADO: Loop infinito - nunca para de tentar
   */
  private attemptReconnection(): void {
    // CRÍTICO: Verificar se foi destruído
    if (this.isDestroyed || !this.reconnectionInProgress) {
      this.logger.log('⏹️ Reconexão cancelada (serviço destruído ou parado)');
      return;
    }

    this.reconnectionService.attemptReconnection().subscribe({
      next: (success) => {
        // Verificar novamente antes de processar resultado
        if (this.isDestroyed) {
          this.logger.log('⏹️ Reconexão cancelada durante tentativa');
          return;
        }

        if (success) {
          // Reconectou com sucesso
          this.connectionMonitor.setConnectionState('connected');
          this.handleReconnection();
        } else {
          // Falhou, tentar novamente SEMPRE
          this.logger.warn('❌ Tentativa de reconexão falhou, tentando novamente...');
          this.attemptReconnection();
        }
      },
      error: (error) => {
        // Verificar se foi destruído
        if (this.isDestroyed) {
          return;
        }

        // Qualquer erro: tentar novamente SEMPRE
        this.logger.error('❌ Erro na reconexão, tentando novamente...', error);
        this.attemptReconnection();
      },
    });
  }

  /**
   * Força reconexão manual (para testes ou botão de reset)
   */
  forceReconnection(): void {
    if (this.isDestroyed) {
      this.logger.warn('⚠️ Não pode forçar reconexão em serviço destruído');
      return;
    }

    this.logger.log('🔧 Reconexão manual forçada');
    
    this.connectionMonitor.setConnectionState('disconnected');
    this.handleDisconnection();
  }

  /**
   * Obtém estado atual da conexão
   */
  getCurrentConnectionState(): ConnectionState {
    return this.connectionMonitor.getCurrentState();
  }

  /**
   * Verifica se está conectado
   */
  isConnected(): boolean {
    return this.connectionMonitor.getCurrentState() === 'connected';
  }

  /**
   * Obtém número de tentativas de reconexão
   */
  getReconnectionAttempts(): number {
    return this.reconnectionService.getCurrentAttempts();
  }

  /**
   * Obtém métricas de conexão
   */
  getMetrics() {
    return this.connectionMonitor.getMetrics();
  }
}
